/**
 * Rechenhilfe in Formeln: steht der Cursor am Ende eines Ausdrucks in `$…$`
 * oder `$$…$$`, erscheint dahinter ein kleiner Ergebnis-Knopf –
 * `= \frac{1}{12}  ≈ 0,0833`, bei Gleichungen `⇒ x = 2`, bei Termen die
 * vereinfachte Form. Tab übernimmt das Ergebnis (Shift+Tab den gerundeten
 * Wert), Antippen genauso. Escape blendet ihn aus.
 *
 * Ersetzt den früheren Auslöser „==": der schrieb ungefragt in den Text,
 * sobald man „== " tippte, und konnte kaum mehr als Grundrechenarten. Jetzt
 * wird nur vorgeschlagen, nie selbst eingefügt.
 *
 * Nebenbei: „frr" in einer Formel wird zu `\frac{}{}` (Cursor im Zähler).
 */

import { Prec, StateEffect, StateField, type EditorState, type Extension, type Transaction } from "@codemirror/state";
import { Decoration, EditorView, ViewPlugin, WidgetType, type DecorationSet, type ViewUpdate } from "@codemirror/view";
import katex from "katex";
import { computeLatex, type MathResult } from "./mathCompute";
import { expandEllipses, isEllipsis, nextTerms } from "./series";

interface MathSpan {
  kind: "display" | "inline";
  /** Position direkt nach dem öffnenden `$`/`$$`. */
  start: number;
}

/**
 * In welcher Formel liegt `pos`, und wo fängt sie an? Zustandsmaschine statt
 * Regex: beim Tippen ist die Formel oft noch nicht geschlossen. `$$` schaltet
 * nur um, solange keine `$…$`-Spanne offen ist (und umgekehrt) – dasselbe
 * Verschachtelungsverbot wie in protect() in markdown/index.ts.
 */
function mathSpanAt(doc: string, pos: number): MathSpan | null {
  let inDisplay = false;
  let inInline = false;
  let start = -1;
  for (let i = 0; i < pos; i++) {
    if (doc[i] === "$" && doc[i + 1] === "$") {
      if (!inInline) {
        inDisplay = !inDisplay;
        start = inDisplay ? i + 2 : -1;
      }
      i++;
      continue;
    }
    if (doc[i] === "$" && !inDisplay) {
      inInline = !inInline;
      start = inInline ? i + 1 : -1;
    }
  }
  if (inDisplay) return { kind: "display", start };
  if (inInline) return { kind: "inline", start };
  return null;
}

interface Candidate {
  pos: number;
  /** Was gerechnet wird. */
  latex: string;
  /** Direkt vor dem Cursor steht ein „=" – das Ergebnis gehört dahinter. */
  eqFrom: number;
}

/** Trennt eine Rechenkette: nur das Stück nach dem letzten „=", „≈" oder „⇒" zählt. */
const CHAIN = /=|\\approx|\\Rightarrow|\\Leftrightarrow|\\iff|\\implies/g;

function candidateAt(state: EditorState): Candidate | null {
  const sel = state.selection;
  if (sel.ranges.length > 1 || !sel.main.empty) return null;
  const pos = sel.main.head;
  const line = state.doc.lineAt(pos);
  // Nur am Ende des Ausdrucks: dahinter darf höchstens das schließende `$`
  // oder ein Zeilenumbruch `\\` stehen. Mitten im Text zu rechnen verwirrt.
  const after = state.doc.sliceString(pos, line.to).trim();
  if (after && !/^(\$|\\\\)/.test(after)) return null;
  const span = mathSpanAt(state.doc.sliceString(0, pos), pos);
  if (!span) return null;

  // Gleichungssystem: der ganze Block zählt, nicht nur die letzte Zeile.
  const whole = state.doc.sliceString(span.start, pos);
  if (/\\begin\{cases\}[\s\S]*\\end\{cases\}\s*$/.test(whole)) {
    return { pos, latex: whole.slice(whole.indexOf("\\begin{cases}")).trim(), eqFrom: -1 };
  }

  let seg = state.doc.sliceString(Math.max(span.start, line.from), pos);
  const lineBreak = seg.lastIndexOf("\\\\");
  if (lineBreak >= 0) seg = seg.slice(lineBreak + 2);
  seg = seg.replace(/\\begin\{[a-z*]+\}/g, "").replace(/\\(quad|qquad|,|;)\s*/g, " ");

  const trimmed = seg.trimEnd();
  const trailingEq = /(^|[^<>!=\\])=$/.test(trimmed);
  let latex: string;
  let eqFrom = -1;
  if (trailingEq) {
    // Position des „=" im echten Text (seg ist um \quad & Co. bereinigt).
    const raw = state.doc.sliceString(line.from, pos);
    eqFrom = line.from + raw.trimEnd().length - 1;
    if (state.doc.sliceString(eqFrom, eqFrom + 1) !== "=") return null;
    const pieces = trimmed.slice(0, -1).split(CHAIN);
    latex = pieces[pieces.length - 1];
  } else {
    // Eine Gleichung (genau ein „=") wird gelöst, eine längere Kette nur ab dem letzten Glied gerechnet.
    const parts = trimmed.split(/\\approx|\\Rightarrow|\\Leftrightarrow|\\iff|\\implies/);
    latex = parts[parts.length - 1];
    if ((latex.match(/=/g) ?? []).length > 1) latex = latex.slice(latex.lastIndexOf("=") + 1);
  }
  latex = latex.trim();
  // Eine nackte Zahl oder Variable ergibt kein Ergebnis, das sich lohnt.
  if (!latex || /^-?[\d.,{}\s]+$/.test(latex) || /^\\?[a-zA-Z]+$/.test(latex)) return null;
  if (!trailingEq) {
    // In einer Tabellenzeile gehört Tab der Zellen-Navigation (keys.ts).
    if (/^\s*\|/.test(line.text)) return null;
    // Ohne getipptes „=" nur, wenn wirklich etwas zu rechnen ist – ein
    // einzelner Bruch als Schreibweise soll keinen Knopf bekommen.
    const fracs = (latex.match(/\\frac/g) ?? []).length;
    if (!OPERATOR.test(latex) && fracs < 2 && !latex.includes("=")) return null;
  }
  return { pos, latex, eqFrom };
}

const OPERATOR = /[+\-*/^!]|\\(?:cdot|times|div|sqrt|binom|sum|prod|int|sin|cos|tan|log|ln|lg|exp)\b/;

/** Reihe fortsetzen oder `…` ausschreiben: ersetzt `from`–Cursor durch `insert`. */
interface SeriesResult {
  ok: true;
  kind: "series";
  from: number;
  insert: string;
  /** Was im Knopf steht. */
  preview: string;
  math: boolean;
}

interface Chip {
  pos: number;
  eqFrom: number;
  result: Exclude<MathResult, { ok: false }> | SeriesResult;
}

/**
 * Aufzählung vor dem Cursor, durch „, " getrennt: `2, 4, 8, ` setzt sich fort
 * (die nächsten drei Glieder), `1, 4, 9, …, 100` wird ausgeschrieben. Geht
 * in Formeln wie im Fließtext.
 */
function seriesAt(state: EditorState): SeriesResult | null {
  const sel = state.selection;
  if (sel.ranges.length > 1 || !sel.main.empty) return null;
  const pos = sel.main.head;
  const line = state.doc.lineAt(pos);
  const after = state.doc.sliceString(pos, line.to).trim();
  if (after && !/^(\$|\\\\)/.test(after)) return null;
  const before = state.doc.sliceString(line.from, pos);
  const math = !!mathSpanAt(state.doc.sliceString(0, pos), pos);

  const open = /,\s*$/.test(before);
  const pieces = before.replace(/,\s*$/, "").split(/,\s+/);
  // Nur das letzte Stück vor dem ersten Glied ist Text davor („Quadratzahlen: 1").
  const terms: string[] = [];
  let start = pos - (before.length - before.trimEnd().length);
  for (let i = pieces.length - 1; i >= 0; i--) {
    const piece = pieces[i].trim();
    // Text vor dem ersten Glied („Quadratzahlen: 1", „$1") abtrennen.
    const term = isEllipsis(piece) ? piece : piece.replace(/^.*[\s$(:]/, "");
    if (!term || (!isEllipsis(term) && !/\d/.test(term))) break;
    terms.unshift(term);
    start = line.from + before.lastIndexOf(term, start - line.from - 1);
    if (term !== piece) break;
  }
  const preview = (list: string[]) => list.join(", ");
  if (open) {
    const next = nextTerms(terms.filter((t) => !isEllipsis(t)), 3);
    if (!next || terms.some(isEllipsis)) return null;
    const lead = /\s$/.test(before) ? "" : " ";
    return { ok: true, kind: "series", from: pos, insert: lead + preview(next), preview: preview(next), math };
  }
  if (terms.length < 3 || !terms.some(isEllipsis) || isEllipsis(terms[terms.length - 1])) return null;
  const full = expandEllipses(terms);
  if (!full) return null;
  const shown = full.length > 7 ? [...full.slice(0, 3), "…", ...full.slice(-2)] : full;
  return { ok: true, kind: "series", from: start, insert: preview(full), preview: preview(shown), math };
}

const setChip = StateEffect.define<Chip | null>();

const chipField = StateField.define<Chip | null>({
  create: () => null,
  update(chip, tr) {
    for (const e of tr.effects) if (e.is(setChip)) return e.value;
    // Jede Änderung oder Cursorbewegung macht den Vorschlag ungültig, bis neu gerechnet ist.
    return tr.docChanged || tr.selection ? null : chip;
  },
  provide: (f) =>
    EditorView.decorations.from(f, (chip): DecorationSet =>
      chip ? Decoration.set([Decoration.widget({ widget: new ChipWidget(chip), side: 1 }).range(chip.pos)]) : Decoration.none
    ),
});

type Solved = Extract<MathResult, { kind: "solve" | "system" }>;

function solutionsTex(r: Solved): string {
  if (r.kind === "system") {
    return r.solutions.map((set) => set.join(",\\; ")).join(" \\quad\\text{oder}\\quad ");
  }
  const v = r.variable;
  if (r.all) return "\\mathbb{L} = \\mathbb{R}";
  if (!r.solutions.length) return "\\mathbb{L} = \\{\\,\\}";
  if (r.solutions.length === 1) return `${v} = ${r.solutions[0]}`;
  return r.solutions.map((s, i) => `${v}_${i + 1} = ${s}`).join(",\\; ");
}

/** Was eingefügt wird – `approx` = gerundeten Wert statt des exakten. */
function insertion(state: EditorState, chip: Chip, approx: boolean): { from: number; to: number; insert: string } {
  const r = chip.result;
  if (r.kind === "series") return { from: r.from, to: chip.pos, insert: r.insert };
  if (r.kind === "solve" || r.kind === "system") {
    return { from: chip.pos, to: chip.pos, insert: ` \\quad\\Rightarrow\\quad ${solutionsTex(r)}` };
  }
  const useApprox = r.kind === "value" && ((approx && !!r.approx) || !!r.rounded);
  const value = r.kind === "value" && approx && r.approx ? r.approx : r.exact;
  if (chip.eqFrom >= 0) {
    // „… =" steht schon da: gerundet ersetzt das „=" durch „≈", sonst nur dahinter.
    if (useApprox) return { from: chip.eqFrom, to: chip.pos, insert: `\\approx ${value}` };
    const space = state.sliceDoc(chip.pos - 1, chip.pos) === " " ? "" : " ";
    return { from: chip.pos, to: chip.pos, insert: space + value };
  }
  return { from: chip.pos, to: chip.pos, insert: useApprox ? ` \\approx ${value}` : ` = ${value}` };
}

function accept(view: EditorView, approx: boolean): boolean {
  const chip = view.state.field(chipField, false);
  if (!chip) return false;
  const change = insertion(view.state, chip, approx);
  view.dispatch({
    changes: change,
    selection: { anchor: change.from + change.insert.length },
    effects: setChip.of(null),
    userEvent: "input.complete",
  });
  view.focus();
  return true;
}

function render(latex: string): string {
  try {
    return katex.renderToString(latex, { throwOnError: false, displayMode: false });
  } catch {
    return "";
  }
}

class ChipWidget extends WidgetType {
  constructor(private readonly chip: Chip) {
    super();
  }
  eq(other: ChipWidget): boolean {
    return other.chip.pos === this.chip.pos && JSON.stringify(other.chip.result) === JSON.stringify(this.chip.result);
  }
  toDOM(view: EditorView): HTMLElement {
    const r = this.chip.result;
    const el = document.createElement("span");
    el.className = "cm-md-mathChip";
    const part = (latex: string, approx: boolean, title: string) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "cm-md-mathChip-part";
      b.title = title;
      b.innerHTML = render(latex);
      b.addEventListener("mousedown", (e) => e.preventDefault());
      b.addEventListener("click", (e) => {
        e.preventDefault();
        accept(view, approx);
      });
      return b;
    };
    if (r.kind === "series") {
      const b = part(r.math ? `\\to ${r.preview}` : "", false, "Reihe einfügen (Tab)");
      if (!r.math) b.textContent = `→ ${r.preview}`;
      el.append(b);
    } else if (r.kind === "solve" || r.kind === "system") {
      el.append(part(`\\Rightarrow ${solutionsTex(r)}`, false, "Lösung einfügen (Tab)"));
    }
    else {
      const sign = r.kind === "value" && r.rounded ? "\\approx" : "=";
      el.append(part(`${sign} ${r.exact}`, false, "Ergebnis einfügen (Tab)"));
      if (r.kind === "value" && r.approx) el.append(part(`\\approx ${r.approx}`, true, "Gerundet einfügen (Shift+Tab)"));
    }
    const hint = document.createElement("kbd");
    hint.textContent = "Tab";
    el.append(hint);
    return el;
  }
  ignoreEvent(): boolean {
    return true;
  }
}

function fromTyping(tr: Transaction): boolean {
  return tr.isUserEvent("input.type");
}

/** „frr" in einer Formel -> `\frac{}{}` mit dem Cursor im Zähler. */
function checkFracTrigger(view: EditorView): boolean {
  const state = view.state;
  const sel = state.selection.main;
  if (!sel.empty) return false;
  const pos = sel.head;
  if (state.sliceDoc(Math.max(0, pos - 3), pos) !== "frr") return false;
  if (/[a-zA-Z]/.test(state.sliceDoc(Math.max(0, pos - 4), pos - 3))) return false;
  const markerStart = pos - 3;
  if (!mathSpanAt(state.sliceDoc(0, markerStart), markerStart)) return false;
  view.dispatch({
    changes: { from: markerStart, to: pos, insert: "\\frac{}{}" },
    selection: { anchor: markerStart + 6 },
  });
  return true;
}

const DELAY_MS = 350;

const assistPlugin = ViewPlugin.fromClass(
  class {
    private timer: number | undefined;
    private token = 0;
    constructor(private readonly view: EditorView) {}
    update(u: ViewUpdate) {
      if (u.docChanged && u.transactions.some(fromTyping) && checkFracTrigger(u.view)) return;
      if (!u.docChanged && !u.selectionSet) return;
      window.clearTimeout(this.timer);
      const token = ++this.token;
      this.timer = window.setTimeout(() => void this.run(token), DELAY_MS);
    }
    private async run(token: number): Promise<void> {
      const series = seriesAt(this.view.state);
      if (series) {
        this.view.dispatch({ effects: setChip.of({ pos: this.view.state.selection.main.head, eqFrom: -1, result: series }) });
        return;
      }
      const cand = candidateAt(this.view.state);
      if (!cand) return;
      const doc = this.view.state.doc;
      const result = await computeLatex(cand.latex);
      // Inzwischen weitergetippt/bewegt: dieser Vorschlag gilt nicht mehr.
      if (token !== this.token || this.view.state.doc !== doc || !result.ok) return;
      if (this.view.state.selection.main.head !== cand.pos) return;
      // „x = 5" nach x aufgelöst ist wieder „x = 5" – kein Vorschlag.
      const flat = (t: string) => t.replace(/\s+/g, "");
      if (result.kind === "solve" && result.solutions.length === 1 && flat(`${result.variable}=${result.solutions[0]}`) === flat(cand.latex)) return;
      this.view.dispatch({ effects: setChip.of({ pos: cand.pos, eqFrom: cand.eqFrom, result }) });
    }
    destroy() {
      window.clearTimeout(this.timer);
    }
  }
);

/**
 * Tab/Shift+Tab/Escape nur, solange ein Vorschlag sichtbar ist – sonst gehen
 * die Tasten wie gewohnt an die Markdown-Tastatur (keys.ts). Deshalb als
 * DOM-Handler mit höchster Priorität: der wird in codeEditor.ts VOR den
 * eigenen Tastaturhörern eingehängt, die Tab sonst als Einrücken verbrauchen.
 */
const assistKeys = Prec.highest(
  EditorView.domEventHandlers({
    keydown: (e, view) => {
      if (!view.state.field(chipField, false)) return false;
      if (e.key === "Tab" && !e.altKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        return accept(view, e.shiftKey);
      }
      if (e.key === "Escape") {
        view.dispatch({ effects: setChip.of(null) });
        return true;
      }
      return false;
    },
  })
);

export function mathAssist(): Extension {
  return [assistKeys, chipField, assistPlugin];
}
